
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { 
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogHeader
} from "@/components/ui/dialog";

type TeamInvitation = {
  id: string;
  email: string;
  company: {
    id: string;
    name: string;
  };
};

export const TeamInviteAlert = ({ 
  userId,
  currentCompanyId 
}: { 
  userId: string;
  currentCompanyId: string;
}) => {
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState<TeamInvitation | null>(null);

  const fetchInvitations = async () => {
    try {
      // First get the user's email directly from the auth user session
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user || !user.email) {
        console.log("No user or email found in session");
        return;
      }
      
      console.log("Fetching invitations for email:", user.email);
      
      // Get all pending invitations for this user's email
      const { data, error } = await supabase
        .from("team_members")
        .select(`
          id,
          email,
          company:companies(id, name)
        `)
        .eq("status", "pending")
        .eq("email", user.email)
        .is("user_id", null);

      if (error) {
        console.error("Error fetching team invitations:", error);
        return;
      }

      // Filter out invitations from the current company if currentCompanyId exists
      let filteredInvitations = data;
      if (currentCompanyId) {
        filteredInvitations = data.filter((invite: any) => 
          invite.company?.id !== currentCompanyId
        );
      }

      if (filteredInvitations && filteredInvitations.length > 0) {
        console.log("Found invitations:", filteredInvitations);
        setInvitations(filteredInvitations as any);
        setShowDialog(true);
      } else {
        console.log("No invitations found");
      }
    } catch (error) {
      console.error("Error fetching team invitations:", error);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchInvitations();
    }
  }, [userId, currentCompanyId]);

  const acceptInvitation = async (invitation: TeamInvitation) => {
    setIsLoading(true);
    setSelectedInvite(invitation);

    try {
      // First, update team member status to active and set user_id
      const { error: teamMemberError } = await supabase
        .from("team_members")
        .update({
          user_id: userId,
          status: "active"
        })
        .eq("id", invitation.id);

      if (teamMemberError) throw teamMemberError;

      // Then, update user's company_id in profiles
      const { error: profileError } = await supabase
        .from("profiles")
        .update({ company_id: invitation.company.id })
        .eq("id", userId);

      if (profileError) throw profileError;

      toast.success(`Successfully joined ${invitation.company.name}`);
      
      // Close dialog and reload page to show new company
      setShowDialog(false);
      window.location.reload();
    } catch (error: any) {
      console.error("Error accepting invitation:", error);
      toast.error(`Failed to join team: ${error.message}`);
    } finally {
      setIsLoading(false);
      setSelectedInvite(null);
    }
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Team Invitations</DialogTitle>
          <DialogDescription>
            You have pending invitations to join other teams
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {invitations.map((invitation) => (
            <div key={invitation.id} className="flex items-center justify-between p-4 border rounded-md">
              <div>
                <p className="font-medium">{invitation.company.name}</p>
                <p className="text-sm text-muted-foreground">
                  You've been invited to join this team
                </p>
              </div>
              <Button 
                onClick={() => acceptInvitation(invitation)}
                disabled={isLoading && selectedInvite?.id === invitation.id}
              >
                {isLoading && selectedInvite?.id === invitation.id 
                  ? "Joining..." 
                  : "Join Team"}
              </Button>
            </div>
          ))}
        </div>
        
        <DialogFooter className="sm:justify-start">
          <Button 
            variant="outline" 
            onClick={() => setShowDialog(false)}
          >
            Decide Later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
